import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User, fetchUser, updateUser, UpdateProfileRequest, setAdminClaim, removeAdminClaim, listAdminUsers } from "./userApi";
import styles from "./UserShared.module.css";
import {
  ArrowLeft,
  Save,
  X,
  User as UserIcon,
  Mail,
  Smartphone,
  Calendar,
  Music,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";

const EditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminActionLoading, setAdminActionLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState<
    | { type: "success" | "error"; message: string; visible: boolean }
    | null
  >(null);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message, visible: true });
    // hide animation
    window.setTimeout(() => {
      setToast((t) => (t ? { ...t, visible: false } : null));
    }, 2200);
    // remove from DOM
    window.setTimeout(() => {
      setToast(null);
    }, 2500);
  };

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [fcmToken, setFcmToken] = useState("");
  const [language, setLanguage] = useState<string>("english");

  useEffect(() => {
    if (id) {
      loadUser(id);
    }
  }, [id]);

  const loadUser = async (userId: string) => {
    try {
      setLoading(true);
      setError(null);

      const userData = await fetchUser(userId);
      
      // Backend'den admin listesi al ve kontrol et
      try {
        const adminList = await listAdminUsers();
        // Bu kullanıcı admin listesinde mi?
        const isAdminUser = adminList.some(admin => 
          admin.uid === userData.user_id || admin.email === userData.email
        );
        userData.isAdmin = isAdminUser;
      } catch (adminErr) {
        console.warn('Admin listesi alınamadı, admin durumu bilinmiyor:', adminErr);
        userData.isAdmin = false;
      }
      
      setUser(userData);
      setName(userData.name || "");
      setEmail(userData.email || "");
      setFcmToken(userData.fcm_token || "");
      setLanguage(userData.language || "english");
    } catch (err: any) {
      console.error("User fetch error:", err);
      setError(err.message || "Kullanıcı yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !user) return;

    if (!name.trim() || !email.trim()) {
      setError("Ad ve e-posta alanları gereklidir");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updateData: UpdateProfileRequest = {
        name: name.trim(),
        email: email.trim(),
        language: language, // panelden seçilen dil
        score: 0, // her zaman 0
      };

      if (fcmToken.trim()) {
        updateData.fcm_token = fcmToken.trim();
      }

      const updated = await updateUser(id, updateData);
      // local state update
      setUser(updated);
      showToast("success", "Değişiklikler kaydedildi");

      // detay sayfasına giderken state de gönder (UserDetail sayfası direkt render edebilsin)
      window.setTimeout(() => {
        navigate(`/users/${id}`, { state: { user: updated } });
      }, 650);
    } catch (err: any) {
      console.error("User update error:", err);
      const msg = err.message || "Kullanıcı güncellenirken hata oluştu";
      setError(msg);
      showToast("error", msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSetAdminClaim = async () => {
    if (!user?.email) {
      showToast("error", "Kullanıcı e-postası bulunamadı");
      return;
    }

    if (!window.confirm(`${user.email} kullanıcısına admin yetkisi vermek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setAdminActionLoading(true);
      const response = await setAdminClaim(user.email);
      
      // Backend'den gelen success mesajını göster
      showToast("success", response.message || "Admin yetkisi verildi! Kullanıcının yeniden giriş yapması gerekiyor.");
      
      // User state'i güncelle
      setUser({ ...user, isAdmin: true });
    } catch (err: any) {
      console.error("Admin claim set error:", err);
      showToast("error", err.message || "Admin yetkisi verilemedi");
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleRemoveAdminClaim = async () => {
    if (!user?.email) {
      showToast("error", "Kullanıcı e-postası bulunamadı");
      return;
    }

    if (!window.confirm(`${user.email} kullanıcısının admin yetkisini kaldırmak istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setAdminActionLoading(true);
      const response = await removeAdminClaim(user.email);
      
      // Backend'den gelen success mesajını göster
      showToast("success", response.message || "Admin yetkisi kaldırıldı");
      
      // User state'i güncelle
      setUser({ ...user, isAdmin: false });
    } catch (err: any) {
      console.error("Admin claim remove error:", err);
      showToast("error", err.message || "Admin yetkisi kaldırılamadı");
    } finally {
      setAdminActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.loadingSpinner}></div>
          <span>Kullanıcı yükleniyor...</span>
        </div>
      </div>
    );
  }

  // Sadece kullanıcı yüklenememişse ve hata varsa hata ekranı göster
  if (error && !user) {
    return (
      <div className={styles.container}>
        <div className={styles.errorWrapper}>
          <h3>Hata</h3>
          <p>{error}</p>
          <button
            onClick={() => navigate("/users")}
            className={styles.errorButton}
          >
            Kullanıcı Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Toast */}
      {toast && (
        <div className={styles.toastContainer} aria-live="polite" aria-atomic="true">
          <div
            className={
              `${styles.toast} ` +
              `${toast.type === "success" ? styles.toastSuccess : styles.toastError} ` +
              `${toast.visible ? styles.toastIn : styles.toastOut}`
            }
          >
            <div className={styles.toastIcon}>
              {toast.type === "success" ? "✅" : "⚠️"}
            </div>
            <div className={styles.toastText}>{toast.message}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <UserIcon className={styles.titleIcon} />
            Kullanıcıyı Düzenle
          </h1>
          <p className={styles.subtitle}>
            {user?.name || "İsimsiz"} kullanıcısının bilgilerini düzenleyin
          </p>
        </div>
        <button
          onClick={() => navigate(`/users/${id}`)}
          className={styles.headerButton}
        >
          <ArrowLeft size={16} />
          Geri Dön
        </button>
      </div>

      {/* Form */}
      <div className={styles.formWrapper}>
        <form onSubmit={handleSubmit} className={styles.modernForm}>
          {/* Sadece kullanıcı yüklüyse ve hata varsa göster */}
          {error && user && (
            <div className={styles.errorAlert}>
              <X size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <UserIcon size={18} />
              Temel Bilgiler
            </h3>

            <div className={styles.formGrid}>
              <div className={styles.inputGroup}>
                <label htmlFor="name" className={styles.label}>
                  Ad Soyad *
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={styles.input}
                  placeholder="Kullanıcının adını girin"
                  required
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="email" className={styles.label}>
                  <Mail size={16} />
                  E-posta *
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  placeholder="kullanici@ornek.com"
                  required
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="language" className={styles.label}>
                  Uygulama Dili
                </label>
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className={styles.input}
                  required
                >
                  <option value="english">İngilizce</option>
                  <option value="turkish">Türkçe</option>
                  <option value="russian">Rusça</option>
                  <option value="french">Fransızca</option>
                  <option value="spanish">İspanyolca</option>
                </select>
              </div>
            </div>
          </div>

          {/* Technical Information */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <Smartphone size={18} />
              Teknik Bilgiler
            </h3>

            <div className={styles.inputGroup}>
              <label htmlFor="fcmToken" className={styles.label}>
                FCM Token
              </label>
              <textarea
                id="fcmToken"
                value={fcmToken}
                onChange={(e) => setFcmToken(e.target.value)}
                className={styles.textarea}
                placeholder="Firebase Cloud Messaging token (opsiyonel)"
                rows={3}
              />
              <p className={styles.inputHelper}>
                Push bildirimleri için kullanılan Firebase token
              </p>
            </div>
          </div>

          {/* Read-only Information */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              Sistem Bilgileri
            </h3>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statIcon} style={{background:'#6366f1'}}>
                  <UserIcon size={20} />
                </span>
                <div className={styles.statContent}>
                  <h3>Kullanıcı ID</h3>
                  <p>{user?.user_id || "-"}</p>
                </div>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statIcon} style={{background:'#10b981'}}>
                  <Calendar size={20} />
                </span>
                <div className={styles.statContent}>
                  <h3>Kayıt Tarihi</h3>
                  <p>{user?.created_at ? new Date(user.created_at).toLocaleString("tr-TR") : "-"}</p>
                </div>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statIcon} style={{background:'#f59e42'}}>
                  <Calendar size={20} />
                </span>
                <div className={styles.statContent}>
                  <h3>Son Giriş</h3>
                  <p>{user?.last_login ? new Date(user.last_login).toLocaleString("tr-TR") : "-"}</p>
                </div>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statIcon} style={{background:'#ef4444'}}>
                  <Music size={20} />
                </span>
                <div className={styles.statContent}>
                  <h3>Atanmış Şarkı</h3>
                  <p>{user?.assigned_song_ids?.length || 0} şarkı</p>
                </div>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statIcon} style={{background:'#0ea5e9'}}>
                  <Smartphone size={20} />
                </span>
                <div className={styles.statContent}>
                  <h3>Bağlı Cihaz</h3>
                  <p>{user?.devices?.length || 0} cihaz</p>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Yetkilendirme */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              <Shield size={18} />
              Admin Yetkilendirme
            </h3>
            <div className={styles.adminSection}>
              <div className={styles.adminStatusCard}>
                <div className={styles.adminStatusHeader}>
                  <div className={styles.adminBadgeLarge} style={{
                    background: user?.isAdmin 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                    color: 'white',
                    boxShadow: user?.isAdmin 
                      ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                      : '0 4px 12px rgba(0, 0, 0, 0.2)'
                  }}>
                    {user?.isAdmin ? (
                      <>
                        <ShieldCheck size={24} strokeWidth={2.5} />
                        <div className={styles.adminBadgeText}>
                          <span className={styles.adminBadgeTitle}>Admin Kullanıcı</span>
                          <span className={styles.adminBadgeSubtitle}>Tüm yetkilere sahip</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <ShieldOff size={24} strokeWidth={2.5} />
                        <div className={styles.adminBadgeText}>
                          <span className={styles.adminBadgeTitle}>Normal Kullanıcı</span>
                          <span className={styles.adminBadgeSubtitle}>Sınırlı yetkiler</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                <div className={styles.adminStatusBody}>
                  <div className={styles.adminStatusInfo}>
                    <div className={styles.adminInfoItem}>
                      <span className={styles.adminInfoLabel}>Durum:</span>
                      <span className={styles.adminInfoValue} style={{
                        color: user?.isAdmin ? '#10b981' : '#6b7280'
                      }}>
                        {user?.isAdmin ? 'Aktif Admin' : 'Standart Kullanıcı'}
                      </span>
                    </div>
                    <div className={styles.adminInfoItem}>
                      <span className={styles.adminInfoLabel}>Panel Erişimi:</span>
                      <span className={styles.adminInfoValue} style={{
                        color: user?.isAdmin ? '#10b981' : '#ef4444'
                      }}>
                        {user?.isAdmin ? 'Tam Erişim' : 'Erişim Yok'}
                      </span>
                    </div>
                    <div className={styles.adminInfoItem}>
                      <span className={styles.adminInfoLabel}>Yönetim Yetkileri:</span>
                      <span className={styles.adminInfoValue} style={{
                        color: user?.isAdmin ? '#10b981' : '#ef4444'
                      }}>
                        {user?.isAdmin ? 'Tüm İşlemler' : 'Sadece Okuma'}
                      </span>
                    </div>
                  </div>

                  <div className={styles.adminActions}>
                    {!user?.isAdmin ? (
                      <button
                        type="button"
                        onClick={handleSetAdminClaim}
                        disabled={adminActionLoading}
                        className={styles.adminButtonGrant}
                      >
                        {adminActionLoading ? (
                          <>
                            <div className={styles.spinner}></div>
                            İşleniyor...
                          </>
                        ) : (
                          <>
                            <ShieldCheck size={18} />
                            Admin Yetkisi Ver
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleRemoveAdminClaim}
                        disabled={adminActionLoading}
                        className={styles.adminButtonRevoke}
                      >
                        {adminActionLoading ? (
                          <>
                            <div className={styles.spinner}></div>
                            İşleniyor...
                          </>
                        ) : (
                          <>
                            <ShieldOff size={18} />
                            Admin Yetkisini Kaldır
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className={styles.adminWarning}>
                <ShieldAlert size={16} />
                <span>
                  <strong>Önemli:</strong> Admin yetkisi değişikliklerinin etkili olması için kullanıcının çıkış yapıp tekrar giriş yapması gerekir.
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={() => navigate(`/users/${id}`)}
              className={styles.cancelButton}
              disabled={saving}
            >
              <X size={16} />
              İptal
            </button>

            <button
              type="submit"
              className={styles.saveButton}
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className={styles.spinner}></div>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Değişiklikleri Kaydet
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;
